import * as path from "@tauri-apps/api/path";
import type { Setter } from "jotai";
import Papa from "papaparse";

import { Logger } from "@moment/logging";
import type { ClientStoreV2 } from "@moment/reboot-differential";

import { readTextFile } from "../filesystem/filesystem.internal";
import { getAttachmentsDirPath } from "../paths/paths.internal";
import { $registerCsvBinding } from "./csv-bindings.atom";

const logger = new Logger("csv-store-sync");

type Row = Record<string, unknown>;

/** Synthetic primary key name, mirroring SQLite's implicit rowid. Always used as the PK. */
export const SYNTHETIC_PK = "rowid";

// ── CSV Header Sanitization ────────────────────────────────────────

/**
 * Sanitize a column name to contain only alphanumeric chars and underscores.
 * Mirrors Rust's sanitize_column_name logic.
 */
export function sanitizeColumnName(header: string): string {
	let result = header.replace(/[^a-zA-Z0-9_]/g, "_");
	if (result === "") {
		result = "_col";
	}
	if (/^\d/.test(result)) {
		result = "_" + result;
	}
	return result;
}

/**
 * Deduplicate an array of column names by appending _2, _3, etc. for collisions.
 */
function deduplicateHeaders(headers: string[]): string[] {
	const seen = new Set<string>();
	const result: string[] = [];

	for (const h of headers) {
		let name = h;
		let counter = 2;
		while (seen.has(name)) {
			name = `${h}_${counter}`;
			counter++;
		}
		seen.add(name);
		result.push(name);
	}

	return result;
}

// ── Table / Filename Mapping ───────────────────────────────────────

export function tableNameFromFilename(filename: string): string {
	return filename.replace(/\.csv$/i, "");
}

// ── CSV Parsing ────────────────────────────────────────────────────

/**
 * Parse CSV text into rows with sanitized headers.
 * Returns headers, rows, and the synthetic primary key.
 * Every row gets a sequential rowid regardless of whether an "id" column exists.
 */
export function parseCsv(text: string): { headers: string[]; rows: Row[]; primaryKey: string } {
	const result = Papa.parse<string[]>(text.trim(), {
		header: false,
		skipEmptyLines: true,
	});

	if (result.data.length === 0) {
		return { headers: [], rows: [], primaryKey: SYNTHETIC_PK };
	}

	const rawHeaders = result.data[0];
	const sanitized = rawHeaders.map(sanitizeColumnName);
	// Prepend SYNTHETIC_PK so deduplicateHeaders bumps any CSV column named "rowid"
	const allHeaders = deduplicateHeaders([SYNTHETIC_PK, ...sanitized]);
	const headers = allHeaders.slice(1); // user-visible headers (without PK)

	const rows: Row[] = [];
	for (let i = 1; i < result.data.length; i++) {
		const fields = result.data[i];
		const row: Row = {};

		row[SYNTHETIC_PK] = i;

		for (let j = 0; j < headers.length; j++) {
			row[headers[j]] = j < fields.length ? fields[j] : "";
		}

		rows.push(row);
	}

	return { headers, rows, primaryKey: SYNTHETIC_PK };
}

// ── CSV Serialization ──────────────────────────────────────────────

/**
 * Serialize rows back to CSV text.
 * Uses provided header order; omits the synthetic primary key column if syntheticPk is provided.
 */
export function serializeCsv(headers: string[], rows: Row[], syntheticPk?: string): string {
	const outputHeaders = syntheticPk ? headers.filter((h) => h !== syntheticPk) : headers;

	const data = rows.map((row) =>
		outputHeaders.map((h) => (row[h] == null ? "" : String(row[h])))
	);

	return Papa.unparse({ fields: outputHeaders, data }, { newline: "\n" }) + "\n";
}

// ── CSV Binding Type ───────────────────────────────────────────────

export type CsvTableBinding = {
	tableName: string;
	filename: string;
	primaryKey: string;
	hasSyntheticPrimaryKey: boolean;
	headers: string[];
	sanitizedHeaders: string[];
};

// ── Import / Remove ────────────────────────────────────────────────

/**
 * Import a CSV file from disk into the store.
 * Reads the file, parses it, registers a binding via the atom, and upserts all rows.
 */
export async function importCsvIntoStore(
	documentId: string,
	filename: string,
	store: ClientStoreV2,
	set: Setter
): Promise<void> {
	const dirPath = await getAttachmentsDirPath(documentId);
	const filePath = await path.join(dirPath, filename);

	const text = await readTextFile(filePath);
	const { headers, rows, primaryKey } = parseCsv(text);
	const tableName = tableNameFromFilename(filename);

	// Extract original (unsanitized) headers for the binding.
	const headerResult = Papa.parse<string[]>(text.trim(), {
		header: false,
		preview: 1,
	});
	const originalHeaders = headerResult.data.length > 0 ? headerResult.data[0] : [];

	const binding: CsvTableBinding = {
		tableName,
		filename,
		primaryKey,
		hasSyntheticPrimaryKey: true,
		headers: originalHeaders,
		sanitizedHeaders: headers,
	};

	set($registerCsvBinding, { documentId, binding });

	// Drop existing table to avoid merging stale data on re-import.
	// Must await settlement so the drop ack doesn't purge subsequent ops.
	if (store.tableNames().includes(tableName)) {
		store.dropTable(tableName);
		await store.allOpsSettled();
	}
	store.createTable(tableName, primaryKey);

	for (const row of rows) {
		store.upsert(tableName, row);
	}

	await store.allOpsSettled();

	logger.info("Imported CSV into store", {
		documentId,
		filename,
		tableName,
		rowCount: rows.length,
	});
}

/**
 * Remove a CSV-backed table from the store.
 */
export function removeCsvFromStore(filename: string, store: ClientStoreV2): void {
	const tableName = tableNameFromFilename(filename);
	store.dropTable(tableName);
}
